import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { WarningCircle } from 'phosphor-react'
import { Box, Button, Typography } from '@mui/material'
import { parse, isTomorrow } from 'date-fns'

import { getWeekDay } from '../../../libs/date'
import useTranslations from '../../../i18n/useTranslations'
import styles from './styles'

const deliveryDatePropType = PropTypes.string
const cancellationDatetimePropType = PropTypes.instanceOf(Date)

const DeliveryDateBody = ({ date }) => {
  const t = useTranslations()

  const parsedDate = parse(date, 'dd/MM/yyyy', new Date())
  const weekDay = getWeekDay(new Date(parsedDate))
  const showTomorrowLabel = isTomorrow(parsedDate)

  return t('date-digest:body:delivery-date', {
    isTomorrow: showTomorrowLabel,
    weekDay,
    date: parsedDate,
    strong: children => <strong>{children}</strong>,
  })
}
DeliveryDateBody.propTypes = {
  date: deliveryDatePropType.isRequired,
}

const CancellationDateBody = ({ datetime }) => {
  const t = useTranslations()

  return t('date-digest:body:cancellation-date', {
    datetime,
    strong: text => <strong>{text}</strong>,
  })
}
CancellationDateBody.propTypes = {
  datetime: cancellationDatetimePropType.isRequired,
}

const DateDigest = ({
  deliveryDate,
  pickupUnscheduled,
  deliveredDate,
  hasIncident,
  returnShippingCode,
  cancellationDatetime,
}) => {
  const t = useTranslations()

  const hasExpectedDeliveryDate = dateString => {
    if (dateString != null) {
      const dateParts = dateString.split('/')

      const date = new Date(dateParts[2], dateParts[1] - 1, dateParts[0])

      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')

      const formattedDate = `${year}-${month}-${day}`

      if (new Date() > new Date(formattedDate)) return null
      else return dateString != null
    }
    return null
  }

  const isCancelled = cancellationDatetime != null
  const hasExpectedDeliveredDate =
    deliveredDate != null && deliveredDate?.length

  const classes = pickupUnscheduled
    ? {
        ...styles.emptyDeliveryDate,
        ...(hasIncident || isCancelled ? styles.hasError : {}),
      }
    : {
        ...styles.deliveryDate,
        ...(hasIncident || isCancelled ? styles.hasError : {}),
      }

  return (
    <Box sx={classes}>
      <Typography variant="subtitle1" component="p" sx={styles.infoText}>
        {isCancelled
          ? t('date-digest:title:cancellation-date')
          : hasExpectedDeliveredDate
          ? t('date-digest:title:delivered-date')
          : t('date-digest:title:expected-delivery-date')}
      </Typography>
      <Typography variant="body1">
        {isCancelled ? (
          <CancellationDateBody datetime={cancellationDatetime} />
        ) : pickupUnscheduled && !hasIncident ? (
          t('date-digest:body:unscheduled-date')
        ) : returnShippingCode ? (
          t('date-digest:body:not-defined-date')
        ) : hasExpectedDeliveryDate(deliveryDate) ? (
          <DeliveryDateBody date={deliveryDate} />
        ) : hasExpectedDeliveredDate ? (
          <DeliveryDateBody date={deliveredDate[0]} />
        ) : (
          t('date-digest:body:not-defined-date')
        )}
      </Typography>
      {isCancelled ? (
        <Box sx={styles.note}>
          <WarningCircle />
          <Typography variant="subtitle1">
            {t('date-digest:footer:not-recoverable-warning')}
          </Typography>
        </Box>
      ) : null}
      {returnShippingCode && (
        <Box mt={1}>
          <Link to={`/shippings-tracking/${returnShippingCode}`}>
            <Button size="small" sx={styles.button}>
              {t('shipping-detail:see-return')}
            </Button>
          </Link>
        </Box>
      )}
    </Box>
  )
}

DateDigest.propTypes = {
  deliveryDate: deliveryDatePropType,
  pickupUnscheduled: PropTypes.bool,
  deliveredDate: PropTypes.array,
  hasIncident: PropTypes.bool,
  cancellationDatetime: cancellationDatetimePropType,
  returnShippingCode: PropTypes.string,
}

export default DateDigest
